/**
 * ShoppingCartContext
 * 
 * React context for managing shopping cart state across the application.
 * Provides cart operations, state management, and persistence.
 * 
 * Features:
 * - Add/remove/update cart items
 * - Quantity management
 * - Cart totals calculation
 * - Local storage persistence
 * - Shopify cart synchronization
 * - Type-safe cart operations
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { ShopifyProduct, ShopifyProductVariant } from '../types/shopify';
import { checkoutAnalyticsService } from '../services/checkoutAnalyticsService';
import { debugError, debugWarn } from '../utils/debugLog';
import { resolveVariantPrice } from '../utils/shopifyHelpers';

// Cart item interface
export interface CartItem {
  id: string; // Unique cart item ID (variant ID + selected options hash)
  productId: string; // Shopify product ID
  variantId: string; // Shopify variant ID
  product: ShopifyProduct; // Full product data
  variant: ShopifyProductVariant; // Selected variant data
  quantity: number; // Item quantity
  selectedOptions: Record<string, string>; // Selected product options (size, color, etc.)
  addedAt: string; // ISO timestamp when item was added
  updatedAt: string; // ISO timestamp when item was last updated
}

// Cart totals interface
export interface CartTotals {
  subtotal: number; // Total before taxes and shipping
  tax: number; // Estimated tax amount
  shipping: number; // Estimated shipping cost
  total: number; // Final total amount
  currency: string; // Currency code (GBP, USD, etc.)
  itemCount: number; // Total number of items
  uniqueItemCount: number; // Number of unique products
}

// Cart state interface
export interface CartState {
  items: CartItem[];
  totals: CartTotals;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// Cart action types
export type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: ShopifyProduct; variant: ShopifyProductVariant; quantity: number; selectedOptions: Record<string, string> } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESTORE_CART'; payload: CartItem[] }
  | { type: 'SYNC_WITH_SHOPIFY'; payload: CartItem[] };

// Cart context interface
export interface CartContextType {
  state: CartState;
  addItem: (product: ShopifyProduct, variant: ShopifyProductVariant, quantity: number, selectedOptions?: Record<string, string>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (variantId: string, selectedOptions?: Record<string, string>) => number;
  isItemInCart: (variantId: string, selectedOptions?: Record<string, string>) => boolean;
  syncWithShopify: () => Promise<void>;
  getCheckoutUrl: () => string | null;
}

// Initial cart state
const initialState: CartState = {
  items: [],
  totals: {
    subtotal: 0,
    tax: 0,
    shipping: 0,
    total: 0,
    currency: 'GBP',
    itemCount: 0,
    uniqueItemCount: 0,
  },
  isLoading: false,
  error: null,
  lastUpdated: null,
};

// Helper function to generate unique cart item ID
const generateCartItemId = (variantId: string, selectedOptions: Record<string, string> = {}): string => {
  const optionsString = Object.keys(selectedOptions)
    .sort()
    .map(key => `${key}:${selectedOptions[key]}`)
    .join('|');
  return `${variantId}${optionsString ? `_${btoa(optionsString)}` : ''}`;
};

interface ResolvedCartItemPricing {
  unitPrice: number;
  currency: string;
  usedFallback: boolean;
}

const resolveCartItemPricing = (item: CartItem): ResolvedCartItemPricing => {
  const resolved = resolveVariantPrice(item.variant, item.product);
  return {
    unitPrice: resolved.amount,
    currency: resolved.currencyCode,
    usedFallback: resolved.usedFallback,
  };
};

// Helper function to calculate cart totals
const calculateTotals = (items: CartItem[]): CartTotals => {
  if (items.length === 0) {
    return {
      subtotal: 0,
      tax: 0,
      shipping: 0,
      total: 0,
      currency: 'GBP',
      itemCount: 0,
      uniqueItemCount: 0,
    };
  }

  const pricedItems = items.map(item => ({
    item,
    pricing: resolveCartItemPricing(item),
  }));

  const subtotal = pricedItems.reduce((sum, entry) => {
    return sum + (entry.pricing.unitPrice * entry.item.quantity);
  }, 0);

  // UK VAT rate (20%) - in a real app, this would be calculated by Shopify
  const taxRate = 0.20;
  const tax = subtotal * taxRate;

  // Simple shipping calculation - in a real app, this would be calculated by Shopify
  const shipping = subtotal >= 50 ? 0 : 4.99; // Free shipping at £50+

  const total = subtotal + tax + shipping;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const uniqueItemCount = items.length;

  // Determine currency from first item, default to GBP
  const currency = pricedItems.length > 0 ? pricedItems[0].pricing.currency : 'GBP';
  
  // Validate all items have consistent UK currency
  if (pricedItems.length > 0) {
    const inconsistentCurrency = pricedItems.find(entry => entry.pricing.currency !== 'GBP');
    if (inconsistentCurrency) {
      debugWarn(`Item with non-GBP currency found: ${inconsistentCurrency.item.product.title} - ${inconsistentCurrency.pricing.currency}`);
    }
  }

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    tax: Math.round(tax * 100) / 100,
    shipping: Math.round(shipping * 100) / 100,
    total: Math.round(total * 100) / 100,
    currency,
    itemCount,
    uniqueItemCount,
  };
};

// Cart reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, variant, quantity, selectedOptions = {} } = action.payload;
      const itemId = generateCartItemId(variant.id, selectedOptions);
      const timestamp = new Date().toISOString();

      // Check if item already exists
      const existingItemIndex = state.items.findIndex(item => item.id === itemId);

      let newItems: CartItem[];

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + quantity, updatedAt: timestamp }
            : item
        );
      } else {
        // Add new item
        const newItem: CartItem = {
          id: itemId,
          productId: product.id,
          variantId: variant.id,
          product,
          variant,
          quantity,
          selectedOptions,
          addedAt: timestamp,
          updatedAt: timestamp,
        };
        newItems = [...state.items, newItem];
      }

      const newTotals = calculateTotals(newItems);

      return {
        ...state,
        items: newItems,
        totals: newTotals,
        lastUpdated: timestamp,
        error: null,
      };
    }

    case 'REMOVE_ITEM': {
      const { itemId } = action.payload;
      const newItems = state.items.filter(item => item.id !== itemId);
      const newTotals = calculateTotals(newItems);
      const timestamp = new Date().toISOString();

      return {
        ...state,
        items: newItems,
        totals: newTotals,
        lastUpdated: timestamp,
        error: null,
      };
    }

    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload;
      const timestamp = new Date().toISOString();

      if (quantity <= 0) {
        // Remove item if quantity is 0 or negative
        const newItems = state.items.filter(item => item.id !== itemId);
        const newTotals = calculateTotals(newItems);

        return {
          ...state,
          items: newItems,
          totals: newTotals,
          lastUpdated: timestamp,
          error: null,
        };
      }

      // Update item quantity
      const newItems = state.items.map(item =>
        item.id === itemId
          ? { ...item, quantity, updatedAt: timestamp }
          : item
      );
      const newTotals = calculateTotals(newItems);

      return {
        ...state,
        items: newItems,
        totals: newTotals,
        lastUpdated: timestamp,
        error: null,
      };
    }

    case 'CLEAR_CART': {
      const timestamp = new Date().toISOString();
      return {
        ...state,
        items: [],
        totals: calculateTotals([]),
        lastUpdated: timestamp,
        error: null,
      };
    }

    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload,
      };
    }

    case 'SET_ERROR': {
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    }

    case 'RESTORE_CART': {
      const items = action.payload;
      const totals = calculateTotals(items);
      return {
        ...state,
        items,
        totals,
        isLoading: false,
        error: null,
      };
    }

    case 'SYNC_WITH_SHOPIFY': {
      const items = action.payload;
      const totals = calculateTotals(items);
      const timestamp = new Date().toISOString();
      return {
        ...state,
        items,
        totals,
        lastUpdated: timestamp,
        isLoading: false,
        error: null,
      };
    }

    default:
      return state;
  }
};

// Create context
const ShoppingCartContext = createContext<CartContextType | undefined>(undefined);

// Context provider props
interface ShoppingCartProviderProps {
  children: ReactNode;
}

// Local storage key
const CART_STORAGE_KEY = 'eruption-radio-cart';

// Context provider component
export const ShoppingCartProvider: React.FC<ShoppingCartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsedCart: CartItem[] = JSON.parse(savedCart);
        // Validate cart items before restoring
        const validItems = parsedCart.filter(item => 
          item.id && item.productId && item.variantId && item.quantity > 0
        );
        if (validItems.length > 0) {
          dispatch({ type: 'RESTORE_CART', payload: validItems });
        }
      }
    } catch (error) {
      debugError('Failed to load cart from localStorage:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load saved cart' });
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
    } catch (error) {
      debugError('Failed to save cart to localStorage:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to save cart' });
    }
  }, [state.items]);

  // Cart operations
  const addItem = useCallback((
    product: ShopifyProduct,
    variant: ShopifyProductVariant,
    quantity: number,
    selectedOptions: Record<string, string> = {}
  ) => {
    if (quantity <= 0) {
      dispatch({ type: 'SET_ERROR', payload: 'Quantity must be greater than 0' });
      return;
    }

    if (!variant.availableForSale) {
      dispatch({ type: 'SET_ERROR', payload: 'This item is not available for sale' });
      return;
    }

    // Check stock availability
    if (variant.quantityAvailable !== null && variant.quantityAvailable !== undefined && variant.quantityAvailable < quantity) {
      dispatch({ type: 'SET_ERROR', payload: `Only ${variant.quantityAvailable} items available` });
      return;
    }

    dispatch({ type: 'ADD_ITEM', payload: { product, variant, quantity, selectedOptions } });
    
    // Track add to cart analytics
    try {
      checkoutAnalyticsService.trackAddToCart(product, variant, quantity);
    } catch (error) {
      debugWarn('Failed to track add to cart analytics:', error);
    }
  }, [dispatch]);

  const removeItem = useCallback((itemId: string) => {
    // Find the item before removing it for analytics
    const itemToRemove = state.items.find(item => item.id === itemId);
    
    dispatch({ type: 'REMOVE_ITEM', payload: { itemId } });
    
    // Track remove from cart analytics
    if (itemToRemove) {
      try {
        checkoutAnalyticsService.trackRemoveFromCart(itemToRemove);
      } catch (error) {
        debugWarn('Failed to track remove from cart analytics:', error);
      }
    }
  }, [dispatch, state.items]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity < 0) {
      dispatch({ type: 'SET_ERROR', payload: 'Quantity cannot be negative' });
      return;
    }

    dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });
  }, [dispatch]);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, [dispatch]);

  const getItemQuantity = useCallback((variantId: string, selectedOptions: Record<string, string> = {}): number => {
    const itemId = generateCartItemId(variantId, selectedOptions);
    const item = state.items.find(item => item.id === itemId);
    return item ? item.quantity : 0;
  }, [state.items]);

  const isItemInCart = useCallback((variantId: string, selectedOptions: Record<string, string> = {}): boolean => {
    return getItemQuantity(variantId, selectedOptions) > 0;
  }, [getItemQuantity]);

  const syncWithShopify = useCallback(async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Import the Shopify cart service
      const { shopifyCartService } = await import('../services/shopifyCartApi');
      
      // Sync local cart with Shopify
      const shopifyCart = await shopifyCartService.syncCart(state.items);
      
      // Validate that all local items are still available
      const validItems = state.items.filter(item => {
        // Check if variant is still available
        return item.variant.availableForSale;
      });

      dispatch({ type: 'SYNC_WITH_SHOPIFY', payload: validItems });
      
      // Store the checkout URL for later use
      if (shopifyCart.checkoutUrl) {
        localStorage.setItem('eruption-radio-checkout-url', shopifyCart.checkoutUrl);
      }
      
    } catch (error) {
      debugError('Failed to sync with Shopify:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to sync cart with Shopify' });
    }
  }, [dispatch, state.items]);

  const getCheckoutUrl = useCallback((): string | null => {
    return localStorage.getItem('eruption-radio-checkout-url');
  }, []);

  const contextValue: CartContextType = useMemo(() => ({
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isItemInCart,
    syncWithShopify,
    getCheckoutUrl,
  }), [
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isItemInCart,
    syncWithShopify,
    getCheckoutUrl,
  ]);

  return (
    <ShoppingCartContext.Provider value={contextValue}>
      {children}
    </ShoppingCartContext.Provider>
  );
};

// Custom hook to use shopping cart context
export const useShoppingCartContext = (): CartContextType => {
  const context = useContext(ShoppingCartContext);
  if (context === undefined) {
    throw new Error('useShoppingCartContext must be used within a ShoppingCartProvider');
  }
  return context;
};

export default ShoppingCartContext;
